import { useEffect, useState } from 'react';
import { ModalSelectGroupGenres } from '../../modal/modalSelectGroupGenres/ModalSelectGroupGenres';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { createGroup, getAllMusicGenres } from '../../../features/sessions/sessionGroups/sessionGroups';
import { authenticateUserFromStorage } from '../../../features/sessions/sessionSlice';
import { toast } from 'react-toastify';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CloseIcon from '@mui/icons-material/Close';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT_PRIMARY,
        bgcolor: BG_CARD,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
    '& textarea': { color: TEXT_PRIMARY },
};

export function CreateGroupForm() {
    const currentUser    = useSelector((state: RootState) => state.session.currentUser);
    const userValidated  = useSelector((state: RootState) => state.session.loggedIn);
    const authHeaders    = useSelector((state: RootState) => state.session.authHeaders);
    const dispatch       = useDispatch<AppDispatch>();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [groupNameForm, setGroupNameForm] = useState('');
    const [descriptionGroupForm, setDescriptionGroupForm] = useState('');
    const [groupPrivacityForm, setGroupPrivacityForm] = useState(0);
    const [selectGroupGenreModalOpenClose, setSelectGroupGenreModalOpenClose] = useState(false);
    const [selectedGenres, setSelectedGenres] = useState<number[]>([]);

    useEffect(() => {
        async function authUserOnRoute() {
            if (!userValidated) {
                await dispatch(authenticateUserFromStorage());
            }
        }
        authUserOnRoute();
    }, [userValidated]);

    useEffect(() => {
        async function getMusicalGenres() {
            if (authHeaders) {
                await dispatch(getAllMusicGenres({ authHeaders: authHeaders }));
            }
        }
        getMusicalGenres();
    }, []);

    async function handleCreateGroup() {
        if (authHeaders && currentUser && currentUser.nickname) {
            const response = await dispatch(
                createGroup({
                    authHeaders: authHeaders,
                    userNickname: currentUser.nickname,
                    postBody: {
                        name: groupNameForm,
                        description: descriptionGroupForm,
                        privacity: groupPrivacityForm,
                        musical_genres: selectedGenres,
                    },
                })
            );

            if (response.payload.errors && response.payload.errors.length > 0) {
                response.payload.errors.map((item: string) => { toast.error(item); });
            } else {
                setDialogOpen(false);
                setGroupNameForm('');
                setDescriptionGroupForm('');
                setGroupPrivacityForm(0);
                setSelectedGenres([]);
            }
        }
    }

    return (
        <>
            {selectGroupGenreModalOpenClose && (
                <ModalSelectGroupGenres
                    selectedGenres={selectedGenres}
                    setSelectedGenres={setSelectedGenres}
                    close={setSelectGroupGenreModalOpenClose}
                />
            )}

            <Button
                variant='contained'
                startIcon={<GroupAddIcon />}
                onClick={() => setDialogOpen(true)}
                sx={{
                    bgcolor: BRAND_RED,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#a93226' },
                }}
            >
                Create Group
            </Button>

            <Dialog
                open={dialogOpen}
                maxWidth='sm'
                fullWidth
                onClose={() => setDialogOpen(false)}
                PaperProps={{
                    sx: { bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '12px' },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                    }}
                >
                    <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>
                        Create Group
                    </Typography>
                    <IconButton
                        size='small'
                        onClick={() => setDialogOpen(false)}
                        sx={{ color: TEXT_SECONDARY }}
                    >
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Nome */}
                    <TextField
                        fullWidth
                        label='Group name'
                        placeholder='Group name'
                        value={groupNameForm}
                        onChange={(e) => setGroupNameForm(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                        size='small'
                        sx={inputSx}
                    />

                    {/* Descrição */}
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label='Description'
                        placeholder='Description'
                        value={descriptionGroupForm}
                        onChange={(e) => setDescriptionGroupForm(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                        size='small'
                        sx={inputSx}
                    />

                    {/* Privacidade */}
                    <Box>
                        <Typography variant='caption' color={TEXT_SECONDARY} sx={{ mb: 0.5, display: 'block' }}>
                            Privacy
                        </Typography>
                        <RadioGroup
                            row
                            value={groupPrivacityForm}
                            onChange={(e) => setGroupPrivacityForm(Number(e.target.value))}
                        >
                            <FormControlLabel
                                value={0}
                                control={
                                    <Radio
                                        size='small'
                                        sx={{ color: TEXT_SECONDARY, '&.Mui-checked': { color: BRAND_RED } }}
                                    />
                                }
                                label={
                                    <Typography variant='body2' color={TEXT_PRIMARY}>
                                        Public
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                value={1}
                                control={
                                    <Radio
                                        size='small'
                                        sx={{ color: TEXT_SECONDARY, '&.Mui-checked': { color: BRAND_RED } }}
                                    />
                                }
                                label={
                                    <Typography variant='body2' color={TEXT_PRIMARY}>
                                        Private
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </Box>

                    {/* Géneros + botão criar */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5 }}>
                        <Button
                            variant='outlined'
                            size='small'
                            startIcon={<MusicNoteIcon />}
                            onClick={() => setSelectGroupGenreModalOpenClose(true)}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.2)',
                                color: TEXT_SECONDARY,
                                textTransform: 'none',
                                '&:hover': { borderColor: 'rgba(255,255,255,0.4)', color: TEXT_PRIMARY },
                            }}
                        >
                            Select Genres
                            {selectedGenres.length > 0 && (
                                <Box
                                    component='span'
                                    sx={{
                                        ml: 1,
                                        px: 0.75,
                                        py: 0.1,
                                        bgcolor: BRAND_RED,
                                        borderRadius: '10px',
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        color: '#fff',
                                    }}
                                >
                                    {selectedGenres.length}
                                </Box>
                            )}
                        </Button>

                        <Button
                            variant='contained'
                            onClick={handleCreateGroup}
                            sx={{
                                bgcolor: BRAND_RED,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { bgcolor: '#a93226' },
                            }}
                        >
                            Create
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
